// One small-multiple card on /trends. Displays a dimension's current avg,
// a delta vs prior, and a sparkline-area shape. The trend direction colours
// the line and area fill (sage / amber / muted ink-faint).

export type DimensionMultipleProps = {
  name: string;
  score: number;
  deltaVsPrior: number | null;
  trajectory: number[]; // oldest -> newest
  trendLabel: "rising" | "falling" | "stable";
  /** Optional one-line note rendered under the chart. */
  sub?: string;
};

const W = 200;
const H = 54;

export function DimensionMultiple({ name, score, deltaVsPrior, trajectory, trendLabel, sub }: DimensionMultipleProps) {
  const direction = trendLabel === "rising" ? "up" : trendLabel === "falling" ? "down" : "flat";

  const path = sparkPath(trajectory);
  const area = areaPath(trajectory);

  return (
    <div className={`small ${direction}`}>
      <div className="h">
        <h4>{name}</h4>
        <span className="v">
          <span className="num">{Math.round(score)}</span>
          {deltaVsPrior != null && Math.abs(deltaVsPrior) >= 0.5 ? (
            <span className={`delta ${deltaVsPrior > 0 ? "up" : "down"}`}>
              {deltaVsPrior > 0 ? "+" : "−"}
              {Math.abs(Math.round(deltaVsPrior))}
            </span>
          ) : null}
        </span>
      </div>
      {trajectory.length >= 2 ? (
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" aria-hidden="true">
          <path className="ar" d={area} />
          <path className="ln" d={path} />
        </svg>
      ) : (
        <div style={{ height: H, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: "rgb(var(--color-ink-muted))", fontFamily: "var(--font-mono)" }}>
          Not enough readings
        </div>
      )}
      {sub ? <span className="sub">{sub}</span> : null}
    </div>
  );
}

function sparkPath(values: number[]): string {
  if (values.length < 2) return "";
  const min = Math.min(...values, 30);
  const max = Math.max(...values, 95);
  const range = max - min || 1;
  return values
    .map((v, i) => {
      const x = (i / (values.length - 1)) * W;
      const y = H - ((v - min) / range) * (H - 6) - 3;
      return `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(" ");
}

function areaPath(values: number[]): string {
  if (values.length < 2) return "";
  return `${sparkPath(values)} L ${W} ${H} L 0 ${H} Z`;
}
